import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import sortBy from 'lodash/sortBy';

import { Spinner, Tabs, Tab } from 'react-bootstrap';

import useMountEffect from 'hooks/useMountEffect';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { fetchContents } from 'store/contents/actions';

import TrainingTab from './TrainingTab';

// ----------------------------------
// Types
// ----------------------------------

interface TabProps {
  tabs: Record<string, string>;
  currentTab: string;
  onTabChange: (key: string | null) => void;
  search: string;
}

interface Content {
  id: string;
  title: string;
  s3FileUrl: string;
  // add other fields as needed
}

interface RootState {
  contents: {
    isLoading: boolean;
    data: Content[];
  };
}

const TrainingTabs: React.FC<TabProps> = ({
  tabs,
  currentTab,
  onTabChange,
  search,
}) => {
  const dispatch = useDispatch();
  const { state } = useLocation() as { state: { forceRefresh?: boolean } | null };

  const { isLoading, data } = useSelector((redux: RootState) => redux.contents);

  const fetchData = React.useCallback(() => {
    dispatch(fetchContents({ search }) as any);
  }, [dispatch, search]);

  useMountEffect(() => {
    if (data.length && !state?.forceRefresh) return;
    fetchData();
  });

  useUpdateEffect(() => {
    fetchData();
  }, [fetchData]);

  const contents = React.useMemo(
    () => sortBy(data, [(o) => o.title.toLowerCase()]),
    [data],
  );

  return (
    <div className="position-relative">
      <Tabs id="training-tabs" activeKey={currentTab} onSelect={onTabChange}>
        {Object.entries(tabs).map(([catId, title]) => (
          <Tab key={catId} eventKey={catId} title={title} mountOnEnter>
            <TrainingTab catId={catId} contents={contents} />
          </Tab>
        ))}
      </Tabs>
      {isLoading && (
        <div
          className="position-absolute top-0 h-100 w-100 d-flex justify-content-center align-items-center rounded"
          style={{ background: 'rgba(0, 0, 0, 0.1)' }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}
    </div>
  );
};

export default TrainingTabs;
